//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useRef, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';

const STORAGE_KEY = 'suppressLicenseWarning';

interface ExpirationBannerProps {
  configData: {
    expiration_date: string;
  };
}

export default function ExpirationBanner({ configData }: ExpirationBannerProps) {
  const expirationDate = new Date(configData.expiration_date);
  const today = new Date();

  const diffTime = expirationDate.getTime() - today.getTime();
  const daysUntilExpiration = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const isExpired = daysUntilExpiration < 0;
  const isWarning = daysUntilExpiration >= 0 && daysUntilExpiration <= 30;

  const stored = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
  const [open, setOpen] = useState(stored && stored === expirationDate.toISOString() ? false : isExpired || isWarning);
  const hasRendered = useRef(false);

  const handleClose = (_event: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    sessionStorage.setItem(STORAGE_KEY, expirationDate.toISOString());
    setOpen(false);
  };

  const handleDontShowAgain = () => {
    localStorage.setItem(STORAGE_KEY, expirationDate.toISOString());
    setOpen(false);
  };

  return (
    <>
      {/* Persistent filled error Alert */}
      {isExpired && (
        <Alert
          severity="error"
          variant="filled"
          sx={{ borderRadius: 0 }}
        >
          Your license has expired.
        </Alert>
      )}

      {/* Auto-dismiss warning in Snackbar */}
      <Snackbar
        open={isWarning && open}
        autoHideDuration={10000}
        onClose={handleClose}
        slotProps={{
          transition: {
            timeout: {
              enter: hasRendered.current ? 225 : 0,
              exit: 225,
            },
            onEntered: () => {
              hasRendered.current = true;
            }
          }
        }}
        sx={{
          width: '100%',
          position: 'static',
          transform: 'none',
          boxShadow: 'none',
          justifyContent: 'center',
        }}
      >
        <Alert
          onClose={handleClose}
          severity="warning"
          variant="filled"
          sx={{ borderRadius: 0, width: '100%' }}
        >
          Your license will expire in {daysUntilExpiration} day{daysUntilExpiration !== 1 && 's'}.
          <Button color="inherit" size="small" onClick={handleDontShowAgain} sx={{ margin: 0, paddingBottom: 0, paddingTop:0 }}>
            {"(Don't show again)"}
          </Button>
        </Alert>
      </Snackbar>
    </>
  );
}
